import React from 'react'
import { useIntl } from 'react-intl'
import { Tooltip, Icon } from '@mui/material';

import { withStyles } from '@mui/styles'

import { fileSizeTransform } from '../logics/file-size-transform.logics'
import { uploadTooltipStyles } from './upload-tooltip.styles'

/**
 * @typedef {object} FileConfig
 * @property {string[]} [accept] - Un array de tipos MIME aceptados (ej. ['image/jpeg', 'application/pdf']).
 * @property {number} [maxSize] - El tamaño máximo del archivo en bytes.
 */

/**
 * @typedef {object} UploadTooltipProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {FileConfig} [file] - La configuración del archivo que define restricciones de carga.
 */

/**
 * Un componente de tooltip que muestra información sobre las restricciones de carga de archivos.
 * 
 * Muestra el tamaño máximo permitido y los tipos de archivo aceptados en un tooltip
 * con icono de ayuda. Si no se proporcionan restricciones, no se renderiza nada.
 * 
 * **Nota:** Este componente requiere `react-intl` para la internacionalización.
 * 
 * @component
 * @example
 * const fileConfig = {
 *   accept: ['image/jpeg', 'image/png', 'application/pdf'],
 *   maxSize: 5242880, // 5 MB
 * };
 * 
 * <UploadTooltip file={fileConfig} />
 * 
 * @param {UploadTooltipProps} props - Las props del componente UploadTooltip.
 */
export const UploadTooltip = withStyles(uploadTooltipStyles)(({ classes, file, ...props }) => {
  const { formatMessage } = useIntl()

  if (!file) return null
  if (!file.accept && !file.maxSize) return null

  return (
    <Tooltip
      data-role="upload tooltip"
      title={
        <div data-role="upload info">
          <div className={classes.container}>
            <span className={classes.title}>
              {formatMessage({ id: 'documents.tooltip.maximumSize' })}
            </span>
            <span className={classes.description} data-role="size info">
              {fileSizeTransform(file.maxSize)}
            </span>
          </div>
          <div className={classes.container}>
            <span className={classes.title}>
              {formatMessage({ id: 'documents.tooltip.acceptedFiles' })}
            </span>
            {file.accept ? (
              <span className={classes.description} data-role="type info">
                {file.accept.map((type) => type.split('/').pop()).join(', ')}
              </span>
            ) : null}
          </div>
        </div>
      }
    >
      <div data-role="upload help label">
        <Icon fontSize="inherit">help_outline</Icon>
      </div>
    </Tooltip>
  )
})
